/**
 * @file VehicleModal.js
 * @version 1.6.5
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-16
 * @modified 2026-10-08
 * @description Add/edit modal for a fleet vehicle. Captures registration, body
 *              type + axle configuration, gross weight + payload, a default
 *              driver + haulier, a rear-lift flag and a compartment layout.
 *
 *              Compartments work in one of two modes:
 *               - FIXED: a stepper sets the count and you type the tonnes for
 *                 each compartment (shown over the lorry body).
 *               - MOVEABLE DOORS: the body has a fixed compartment count, and
 *                 each compartment has a min–max tonnage RANGE (1T increments).
 *                 The doors partition the body so the sizes always sum to the
 *                 payload. The rear-lift flag is an AXLE rule on the LOADED weight
 *                 that only bites at FULL payload (majority of the load over the
 *                 front half with a rear lift, over the rear without) — applied to
 *                 each day's load in transport planning. The per-job layout is
 *                 chosen later. The modal previews the most BALANCED valid
 *                 configuration (symmetrical, or biased to the correct half).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './VehicleModal.css';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { moveableRangeConfigs, pickBalancedPreview, configHalfSums } from '../utils/vehicleLoad';
import FindInSearchButton from './FindInSearchButton';
import { formatQuantity } from '../utils/numberFormatters';

const TYPE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'rigid', label: 'Rigid' },
  { value: 'articulated', label: 'Articulated' },
  { value: 'bag', label: 'Bag' }
];

// Lorry image + the per-type geometry class for the compartment overlay.
// Moffett-fitted bag bodies use a different image (forklift on the rear).
function lorryArtFor(vehicleType, hasMoffett) {
  if (vehicleType === 'articulated') return { src: '/ArticFull.png', cls: 'type-artic' };
  if (vehicleType === 'bag') {
    return hasMoffett
      ? { src: '/BagFull_Moffett.png', cls: 'type-bag-moffett' }
      : { src: '/BagFull.png', cls: 'type-bag' };
  }
  return { src: '/LorryFull.png', cls: 'type-rigid' };
}

// A Moffett (truck-mounted forklift) is carried on the rear of a bag body and
// weighs ~1 tonne, so it reduces usable payload by 1t when fitted.
const MOFFETT_PAYLOAD_COST = 1;

const CONFIG_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: '4x2', label: '4x2' },
  { value: '6x2', label: '6x2' },
  { value: '6x4', label: '6x4' },
  { value: '8x2', label: '8x2' },
  { value: '8x4', label: '8x4' },
  { value: '8x6', label: '8x6' }
];

const MAX_COMPARTMENTS = 8;
const round = (n) => Math.round(n * 1000) / 1000;
// Tonnes by the quantity rule: "8.00", "8.50", "8.375" (the caller adds the T).
const fmt = (n) => formatQuantity(n);

// Door slot tonnes inputs are keyboard-entry only — no mouse-wheel and no
// up/down arrow stepping (so the value never changes by accident while the row
// is hovered / focused).
const blockNumberScroll = (e) => e.currentTarget.blur();
const blockArrowKeys = (e) => {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
};

function VehicleModal({ isOpen, vehicle, onClose, onSaved }) {
  const isEdit = !!(vehicle && vehicle.id);

  const [registration, setRegistration] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [axleConfiguration, setAxleConfiguration] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [payload, setPayload] = useState('');
  const [defaultDriverId, setDefaultDriverId] = useState('');
  const [defaultHaulierId, setDefaultHaulierId] = useState('');
  const [rearLift, setRearLift] = useState(false);
  const [hasMoffett, setHasMoffett] = useState(false);
  const [hasMoveableDoors, setHasMoveableDoors] = useState(false);

  // Lorry image shown right now + a flag driving the eased cross-fade when the
  // selected type / Moffett changes the image.
  const [displaySrc, setDisplaySrc] = useState('/LorryFull.png');
  const [fading, setFading] = useState(false);
  const [compartmentCount, setCompartmentCount] = useState(0);
  const [compartmentCaps, setCompartmentCaps] = useState([]); // string[] — fixed mode
  const [compartmentRanges, setCompartmentRanges] = useState([]); // [{ min, max }] strings — moveable mode (front→back)
  const [frontHalfCap, setFrontHalfCap] = useState(''); // moveable — front-half capacity cap (T)
  const [rearHalfCap, setRearHalfCap] = useState('');   // moveable — rear-half capacity cap (T)

  const [drivers, setDrivers] = useState([]);
  const [hauliers, setHauliers] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset the form whenever the modal opens (add) or the target vehicle changes.
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setRegistration(vehicle?.registration || '');
    setVehicleType(vehicle?.vehicle_type || '');
    setAxleConfiguration(vehicle?.axle_configuration || '');
    setGrossWeight(vehicle?.gross_weight != null ? String(vehicle.gross_weight) : '');
    setPayload(vehicle?.payload != null ? String(vehicle.payload) : '');
    setDefaultDriverId(vehicle?.default_driver_id ? String(vehicle.default_driver_id) : '');
    setDefaultHaulierId(vehicle?.default_haulier_id ? String(vehicle.default_haulier_id) : '');
    setRearLift(!!vehicle?.rear_lift);
    const moffett = !!vehicle?.has_moffett;
    setHasMoffett(moffett);
    setHasMoveableDoors(!!vehicle?.has_moveable_doors);
    // Show the right image immediately on open (no fade for the initial load).
    setDisplaySrc(lorryArtFor(vehicle?.vehicle_type || '', moffett).src);
    setFading(false);
    const caps = Array.isArray(vehicle?.compartments)
      ? vehicle.compartments.map(c => (c?.capacity != null ? String(c.capacity) : ''))
      : [];
    const ranges = Array.isArray(vehicle?.compartment_ranges)
      ? vehicle.compartment_ranges.map(r => ({
          min: r?.min != null ? String(r.min) : '',
          max: r?.max != null ? String(r.max) : ''
        }))
      : [];
    const count = vehicle?.compartment_count != null
      ? vehicle.compartment_count
      : (vehicle?.has_moveable_doors ? ranges.length : caps.length);
    setCompartmentCount(count);
    setCompartmentCaps(caps);
    setCompartmentRanges(ranges);
    setFrontHalfCap(vehicle?.front_half_cap != null ? String(vehicle.front_half_cap) : '');
    setRearHalfCap(vehicle?.rear_half_cap != null ? String(vehicle.rear_half_cap) : '');
  }, [isOpen, vehicle]);

  const fetchOptions = useCallback(async () => {
    try {
      const [dRes, hRes] = await Promise.all([
        fetch(`${API_URL}/api/transport/drivers`, { credentials: 'include' }),
        fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' })
      ]);
      const dData = await dRes.json();
      const hData = await hRes.json();
      if (dData.success) setDrivers(dData.drivers.map(d => ({ id: d.id, name: d.name })));
      if (hData.success) setHauliers(hData.hauliers.map(h => ({ id: h.id, name: h.name })));
    } catch (err) {
      console.error('Error loading driver/haulier options:', err);
    }
  }, []);

  useEffect(() => { if (isOpen) fetchOptions(); }, [isOpen, fetchOptions]);

  // ── Derived values (safe to compute even when closed) ────────
  const payloadNum = parseFloat(payload);
  const basePayload = Number.isFinite(payloadNum) ? payloadNum : 0;
  // Moffett is a bag-body option: enabled/applied only when the type is 'bag'.
  // A fitted Moffett costs ~1t of usable payload.
  const moffettActive = vehicleType === 'bag' && hasMoffett;
  const effPayload = round(Math.max(0, basePayload - (moffettActive ? MOFFETT_PAYLOAD_COST : 0)));
  const hasPayload = effPayload > 0;

  // Keep the per-compartment range rows in step with the compartment count.
  useEffect(() => {
    setCompartmentRanges(prev => {
      const next = prev.slice(0, compartmentCount);
      while (next.length < compartmentCount) next.push({ min: '', max: '' });
      return next;
    });
  }, [compartmentCount]);

  // Lorry art (image + geometry class) for the current selection, and the
  // eased cross-fade when it changes.
  const lorryArt = lorryArtFor(vehicleType, hasMoffett);
  useEffect(() => {
    if (!isOpen) return undefined;
    if (lorryArt.src === displaySrc) return undefined;
    setFading(true);
    const t = setTimeout(() => { setDisplaySrc(lorryArt.src); setFading(false); }, 260);
    return () => clearTimeout(t);
  }, [lorryArt.src, displaySrc, isOpen]);
  const displayCls = lorryArtFor(
    displaySrc.includes('Artic') ? 'articulated' : displaySrc.includes('Bag') ? 'bag' : 'rigid',
    displaySrc.includes('Moffett')
  ).cls;

  // A vehicle-shaped object from the current form state, fed to the shared
  // range-config solver to (a) preview a valid layout and (b) count how many
  // layouts satisfy the ranges + half caps + rear-lift weight rule.
  const vehicleLikeForConfig = {
    has_moveable_doors: true,
    rear_lift: rearLift,
    payload: effPayload,
    front_half_cap: frontHalfCap.trim() === '' ? null : parseFloat(frontHalfCap),
    rear_half_cap: rearHalfCap.trim() === '' ? null : parseFloat(rearHalfCap),
    compartment_ranges: compartmentRanges.slice(0, compartmentCount).map(r => {
      const min = parseFloat(r.min);
      const max = parseFloat(r.max);
      return {
        min: Number.isFinite(min) ? min : 0,
        max: Number.isFinite(max) ? max : null
      };
    })
  };
  const rangesComplete = hasMoveableDoors && compartmentCount > 0 &&
    compartmentRanges.slice(0, compartmentCount).every(r =>
      Number.isFinite(parseFloat(r.min)) && Number.isFinite(parseFloat(r.max)));
  const moveConfigs = (hasMoveableDoors && hasPayload && rangesComplete)
    ? moveableRangeConfigs(vehicleLikeForConfig, effPayload)
    : [];
  const configCount = moveConfigs.length;
  // Preview the most BALANCED valid layout (symmetrical front/rear if the payload
  // allows, else biased to the correct half for the rear-lift rule). sizes are
  // already front→back (index 0 = front), matching the left→right layout below.
  const previewCfg = moveConfigs.length ? pickBalancedPreview(vehicleLikeForConfig, moveConfigs) : null;
  const previewSizesF2B = previewCfg ? previewCfg.sizes.slice().reverse() : [];
  const previewHalves = previewCfg ? configHalfSums(previewCfg) : null;
  const previewSymmetrical = previewHalves
    ? Math.abs(previewHalves.frontSum - previewHalves.rearSum) < 1e-6 : false;

  // Re-measure slide-out buttons when the body height changes.
  useSlideOutButton([saving, compartmentCount, compartmentRanges.length, hasMoveableDoors, isOpen]);

  if (!isOpen) return null;

  // ── Fixed-compartment helpers ────────────────────────────────
  const setCompartmentCapacity = (index, value) => {
    setCompartmentCaps(prev => {
      const next = prev.slice();
      while (next.length <= index) next.push('');
      next[index] = value;
      return next;
    });
  };
  const fixedTotal = Array.from({ length: compartmentCount }).reduce((sum, _, i) => {
    const n = parseFloat(compartmentCaps[i]);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  // ── Moveable-door helpers ────────────────────────────────────
  const setRangeField = (i, field, value) =>
    setCompartmentRanges(prev => {
      const next = prev.slice();
      while (next.length <= i) next.push({ min: '', max: '' });
      next[i] = { ...next[i], [field]: value };
      return next;
    });

  // Preview segments from one valid configuration (front→back).
  let cum = 0;
  const segments = hasPayload
    ? previewSizesF2B.map((size) => {
        const left = cum; cum += size;
        return {
          cap: round(size),
          leftPct: (left / effPayload) * 100,
          widthPct: (size / effPayload) * 100
        };
      })
    : [];

  const driverOptions = [
    { value: '', label: 'None' },
    ...drivers.map(d => ({ value: String(d.id), label: d.name }))
  ];
  const haulierOptions = [
    { value: '', label: 'None' },
    ...hauliers.map(h => ({ value: String(h.id), label: h.name }))
  ];

  const stepCount = (delta) =>
    setCompartmentCount(c => Math.max(0, Math.min(MAX_COMPARTMENTS, c + delta)));

  const handleSave = async () => {
    const reg = registration.trim();
    if (!reg) { setError('Registration is required'); return; }
    if (reg.length > 20) { setError('Registration must be 20 characters or less'); return; }
    if (hasMoveableDoors) {
      if (!hasPayload) {
        setError('Set a payload (tonnes) so moveable-door compartments can be calculated');
        return;
      }
      if (compartmentCount < 1) {
        setError('Set at least one compartment for a moveable-door body');
        return;
      }
      for (let i = 0; i < compartmentCount; i++) {
        const mn = parseFloat(compartmentRanges[i]?.min);
        const mx = parseFloat(compartmentRanges[i]?.max);
        if (!Number.isFinite(mn) || !Number.isFinite(mx)) {
          setError(`Enter a min and max tonnage for compartment ${i + 1}`);
          return;
        }
        if (mn > mx) {
          setError(`Compartment ${i + 1}: min can’t be greater than max`);
          return;
        }
      }
      if (moveConfigs.length === 0) {
        setError('No compartment layout fits these ranges and payload. Widen a range.');
        return;
      }
    }

    setSaving(true);
    setError('');
    const payloadBody = {
      registration: reg,
      grossWeight: grossWeight.trim() === '' ? null : grossWeight,
      payload: payload.trim() === '' ? null : payload,
      vehicleType: vehicleType || null,
      axleConfiguration: axleConfiguration || null,
      defaultDriverId: defaultDriverId || null,
      defaultHaulierId: defaultHaulierId || null,
      rearLift,
      hasMoffett: vehicleType === 'bag' ? hasMoffett : false,
      hasMoveableDoors,
      compartmentCount,
      doorPositions: [],
      compartmentRanges: hasMoveableDoors
        ? compartmentRanges.slice(0, compartmentCount).map(r => ({ min: r.min, max: r.max }))
        : [],
      frontHalfCap: hasMoveableDoors ? frontHalfCap : '',
      rearHalfCap: hasMoveableDoors ? rearHalfCap : '',
      compartments: hasMoveableDoors
        ? []
        : Array.from({ length: compartmentCount }, (_, i) => ({ capacity: compartmentCaps[i] ?? '' }))
    };

    try {
      const url = isEdit
        ? `${API_URL}/api/transport/vehicles/${vehicle.id}`
        : `${API_URL}/api/transport/vehicles`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadBody)
      });
      const data = await res.json();
      if (data.success) {
        onSaved(data.vehicle);
      } else {
        setError(data.error || 'Failed to save vehicle');
      }
    } catch (err) {
      setError('Failed to save vehicle. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal-content vehicle-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `Edit Vehicle${registration ? ` — ${registration}` : ''}` : 'Add New Vehicle'}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <div className="modal-body" style={{ overflow: 'visible' }}>
          {error && <div className="vehicle-modal-error">{error}</div>}

          {/* Lorry image with compartment / door overlay. The image AND the
              overlay live in one transformed frame so the overlay always tracks
              the image, and per-type framing is a transform (no reflow). */}
          <div className={`vm-lorry ${displayCls} ${fading ? 'vm-fading' : ''}`}>
            <div className="vm-lorry-frame">
              <img className="vm-lorry-img" src={displaySrc} alt="Lorry" />

            {hasMoveableDoors ? (
              !hasPayload ? (
                <div className="vm-compartments-empty">
                  Set a payload (tonnes) below, then enter each compartment’s size range to preview a layout.
                </div>
              ) : segments.length === 0 ? (
                <div className="vm-compartments-empty">
                  Enter every compartment’s min &amp; max below to preview a valid layout.
                </div>
              ) : (
                <div className="vm-doors-overlay">
                  {segments.map((seg, i) => (
                    <div
                      key={`seg-${i}`}
                      className="vm-door-seg"
                      style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%` }}
                    >
                      <span className="vm-door-seg-index">{i + 1}</span>
                      <span className="vm-door-seg-value">
                        <span className="vm-door-seg-num">{seg.cap}</span>
                        <span className="vm-comp-unit">t</span>
                      </span>
                    </div>
                  ))}
                </div>
              )
            ) : compartmentCount === 0 ? (
              <div className="vm-compartments-empty">
                No compartments — use the stepper below to split the body and set tonnes per compartment.
              </div>
            ) : (
              <div className="vm-compartments">
                {Array.from({ length: compartmentCount }).map((_, i) => (
                  <label key={i} className={`vm-comp ${compartmentCaps[i] ? 'vm-comp-filled' : ''}`}>
                    <span className="vm-comp-index">{i + 1}</span>
                    <span className="vm-comp-value">
                      <input
                        type="number"
                        className="vm-comp-input"
                        value={compartmentCaps[i] ?? ''}
                        onChange={(e) => setCompartmentCapacity(i, e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.1"
                        disabled={saving}
                        aria-label={`Compartment ${i + 1} capacity in tonnes`}
                      />
                      <span className="vm-comp-unit">t</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            </div>
          </div>

          {/* Compartment count stepper + mode toggle */}
          <div className="vm-comp-controls">
            <div className="vm-comp-stepper">
              <span className="vm-comp-stepper-label">Compartments</span>
              <div className="vm-stepper">
                <button
                  type="button"
                  className="vm-stepper-btn"
                  onClick={() => stepCount(-1)}
                  disabled={saving || compartmentCount === 0}
                  title="Fewer compartments"
                >
                  <span className="material-symbols-rounded">remove</span>
                </button>
                <span className="vm-comp-count">{compartmentCount}</span>
                <button
                  type="button"
                  className="vm-stepper-btn"
                  onClick={() => stepCount(1)}
                  disabled={saving || compartmentCount >= MAX_COMPARTMENTS}
                  title="More compartments"
                >
                  <span className="material-symbols-rounded">add</span>
                </button>
              </div>
              {!hasMoveableDoors && compartmentCount > 0 && (
                <span className="vm-comp-total">
                  Total <strong>{formatQuantity(fixedTotal)} T</strong>
                </span>
              )}
            </div>

            <div className="vm-comp-mode">
              <label htmlFor="veh-moveable-doors">Moveable doors</label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="veh-moveable-doors"
                    type="checkbox"
                    checked={hasMoveableDoors}
                    onChange={(e) => setHasMoveableDoors(e.target.checked)}
                    disabled={saving}
                  />
                  <label htmlFor="veh-moveable-doors"></label>
                </div>
              </div>
            </div>
          </div>

          {/* Compartment ranges + half caps editor (moveable-door mode only) */}
          {hasMoveableDoors && compartmentCount > 0 && (
            <div className="vm-ranges-editor">
              <span className="vm-doors-hint">
                Set each compartment’s min–max size (tonnes, front→back). The doors partition the body, so a
                valid layout picks a size in every range that sums to the payload. The <strong>Rear Lift</strong>
                flag sets the weight rule: only when a vehicle is loaded to its <strong>full payload</strong>,
                most of the load must sit over the {rearLift ? 'front' : 'rear'} half — checked against each day’s
                load in transport planning. Part-loaded vehicles aren’t constrained.
              </span>
              <div className="vm-ranges-grid">
                {Array.from({ length: compartmentCount }).map((_, i) => (
                  <div key={i} className="vm-range-row">
                    <span className="vm-range-index">{i + 1}{i === 0 ? ' (front)' : i === compartmentCount - 1 ? ' (rear)' : ''}</span>
                    <input
                      type="number"
                      className="vm-range-input"
                      value={compartmentRanges[i]?.min ?? ''}
                      onChange={(e) => setRangeField(i, 'min', e.target.value)}
                      onWheel={blockNumberScroll}
                      onKeyDown={blockArrowKeys}
                      placeholder="min"
                      min="0"
                      step="1"
                      disabled={saving}
                      aria-label={`Compartment ${i + 1} minimum tonnes`}
                    />
                    <span className="vm-range-dash">–</span>
                    <input
                      type="number"
                      className="vm-range-input"
                      value={compartmentRanges[i]?.max ?? ''}
                      onChange={(e) => setRangeField(i, 'max', e.target.value)}
                      onWheel={blockNumberScroll}
                      onKeyDown={blockArrowKeys}
                      placeholder="max"
                      min="0"
                      step="1"
                      disabled={saving}
                      aria-label={`Compartment ${i + 1} maximum tonnes`}
                    />
                    <span className="vm-range-unit">T</span>
                  </div>
                ))}
              </div>
              <div className="vm-half-caps">
                <div className="vm-half-cap">
                  <label>Front-half cap <span className="vm-label-hint">(T, optional)</span></label>
                  <input
                    type="number"
                    className="vm-range-input"
                    value={frontHalfCap}
                    onChange={(e) => setFrontHalfCap(e.target.value)}
                    onWheel={blockNumberScroll}
                    onKeyDown={blockArrowKeys}
                    placeholder="—"
                    min="0"
                    step="1"
                    disabled={saving}
                  />
                </div>
                <div className="vm-half-cap">
                  <label>Rear-half cap <span className="vm-label-hint">(T, optional)</span></label>
                  <input
                    type="number"
                    className="vm-range-input"
                    value={rearHalfCap}
                    onChange={(e) => setRearHalfCap(e.target.value)}
                    onWheel={blockNumberScroll}
                    onKeyDown={blockArrowKeys}
                    placeholder="—"
                    min="0"
                    step="1"
                    disabled={saving}
                  />
                </div>
              </div>
              {hasPayload && rangesComplete && (
                <span className={`vm-config-count ${configCount === 0 ? 'vm-config-none' : ''}`}>
                  <strong>{configCount}</strong> valid configuration{configCount === 1 ? '' : 's'}
                  {configCount === 0 && ' — widen a range'}
                </span>
              )}
              {previewHalves && (
                <span className={`vm-balance-hint ${previewSymmetrical ? 'vm-balance-sym' : 'vm-balance-biased'}`}>
                  <span className="material-symbols-rounded">
                    {previewSymmetrical ? 'balance' : (rearLift ? 'align_horizontal_left' : 'align_horizontal_right')}
                  </span>
                  {previewSymmetrical
                    ? `Symmetrical — ${fmt(previewHalves.frontSum)}T front / ${fmt(previewHalves.rearSum)}T rear`
                    : `${rearLift ? 'Front' : 'Rear'}-biased — ${fmt(previewHalves.frontSum)}T front / ${fmt(previewHalves.rearSum)}T rear (heavier over the ${rearLift ? 'front' : 'rear'}${rearLift ? ', rear lift fitted' : ''})`}
                </span>
              )}
            </div>
          )}

          {/* Spec fields — row 1 */}
          <div className="vm-field-grid row-1">
            <div className="form-group">
              <label>Registration <span style={{ color: 'var(--color-error)' }}>*</span></label>
              <input
                type="text"
                className="styled-input"
                value={registration}
                onChange={(e) => setRegistration(e.target.value.toUpperCase())}
                placeholder="e.g. AB12 CDE"
                maxLength={20}
                disabled={saving}
                style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}
              />
            </div>

            <div className="form-group">
              <label>Type</label>
              <SearchableDropdown
                options={TYPE_OPTIONS}
                value={vehicleType}
                onChange={setVehicleType}
                placeholder="Select type..."
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Configuration <span className="vm-label-hint">(axles)</span></label>
              <SearchableDropdown
                options={CONFIG_OPTIONS}
                value={axleConfiguration}
                onChange={setAxleConfiguration}
                placeholder="Select..."
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Moffett</label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="veh-moffett"
                    type="checkbox"
                    checked={vehicleType === 'bag' && hasMoffett}
                    onChange={(e) => setHasMoffett(e.target.checked)}
                    disabled={saving || vehicleType !== 'bag'}
                  />
                  <label htmlFor="veh-moffett"></label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Rear Lift</label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="veh-rear-lift"
                    type="checkbox"
                    checked={rearLift}
                    onChange={(e) => setRearLift(e.target.checked)}
                    disabled={saving}
                  />
                  <label htmlFor="veh-rear-lift"></label>
                </div>
              </div>
            </div>
          </div>

          {/* Spec fields — row 2 */}
          <div className="vm-field-grid row-2">
            <div className="form-group">
              <label>Gross Weight <span className="vm-label-hint">(T)</span></label>
              <input
                type="number"
                className="styled-input"
                value={grossWeight}
                onChange={(e) => setGrossWeight(e.target.value)}
                placeholder="e.g. 32"
                min="0"
                step="0.1"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>
                Payload <span className="vm-label-hint">(T){moffettActive ? ` · −1 Moffett → ${fmt(effPayload)}T usable` : ''}</span>
              </label>
              <input
                type="number"
                className="styled-input"
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                placeholder="e.g. 18"
                min="0"
                step="0.1"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Default Driver</label>
              <SearchableDropdown
                options={driverOptions}
                value={defaultDriverId}
                onChange={setDefaultDriverId}
                placeholder="Select driver..."
                searchPlaceholder="Search drivers..."
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Default Haulier</label>
              <SearchableDropdown
                options={haulierOptions}
                value={defaultHaulierId}
                onChange={setDefaultHaulierId}
                placeholder="Select haulier..."
                searchPlaceholder="Search hauliers..."
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
            <span className="btn-label">Cancel</span>
          </button>
          <FindInSearchButton type="vehicle" record={vehicle} onClose={onClose} />
          <button className="btn-slideout" onClick={handleSave} disabled={saving || !registration.trim()}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">{saving ? 'hourglass_top' : 'save'}</span></div>
            <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default VehicleModal;
